import { useEffect, useRef, useState } from "react";
import { MapPin, LocateFixed, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";

export function AddressSearch({ value, onSelect, placeholder, testid, accent = "blue" }) {
  const { locale } = useI18n();
  const [q, setQ] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value?.label || ""); }, [value?.label]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (text) => {
    setQ(text);
    if (timer.current) clearTimeout(timer.current);
    if (text.trim().length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/public/geocode", { params: { q: text, locale } });
        setResults(data); setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 450);
  };

  const choose = (r) => { onSelect(r); setQ(r.label); setOpen(false); };

  const flagEmoji = (cc) => {
    if (!cc || cc.length !== 2) return "📍";
    return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.get("/public/reverse", { params: { lat: pos.coords.latitude, lon: pos.coords.longitude, locale } });
        choose(data);
      } catch { toast.error("Impossibile rilevare la posizione. Riprova."); }
      finally { setLocating(false); }
    }, () => setLocating(false), { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 text-${accent}-600`} strokeWidth={1.5} />
        <Input
          value={q}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-20 h-12 bg-white"
          data-testid={testid}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
          {q && (
            <button type="button" onClick={() => { setQ(""); setResults([]); onSelect(null); }} className="text-slate-400 hover:text-slate-600 p-1"><X size={15} /></button>
          )}
          <button type="button" onClick={useMyLocation} title="Usa la mia posizione" data-testid={`${testid}-geo`} className="text-slate-500 hover:text-blue-700 p-1">
            {locating ? <Loader2 size={16} className="animate-spin" /> : <LocateFixed size={16} />}
          </button>
        </div>
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-[1000] mt-2 w-full bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => choose(r)}
              data-testid={`${testid}-result-${i}`}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-0 border-slate-100 flex gap-3 items-start"
            >
              <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>{flagEmoji(r.country_code)}</span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {r.city || r.address || r.label}{r.postcode ? <span className="text-slate-400 font-normal"> · {r.postcode}</span> : null}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {[r.region, r.country].filter(Boolean).join(", ") || r.label}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
